import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Plus, Trash2, Save, Repeat, Rocket, CalendarCheck, GripVertical } from "lucide-react";
import { withKey } from "../lib/listKeys";

const TYPE_META = {
  install: { label: "Install drip", icon: Rocket, color: "text-primary" },
  booking: { label: "Booking drip", icon: CalendarCheck, color: "text-emerald-400" },
};

function SequenceCard({ seq, onSaved }) {
  const qc = useQueryClient();
  const [steps, setSteps] = useState(() => (seq.steps || []).map(withKey));
  const [name, setName] = useState(seq.name);
  const [active, setActive] = useState(seq.active);
  const [dirty, setDirty] = useState(false);
  const meta = TYPE_META[seq.type] || TYPE_META.install;
  const Icon = meta.icon;

  const update = (i, k, v) => {
    setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, [k]: v } : st)));
    setDirty(true);
  };
  const addStep = () => { setSteps((s) => [...s, withKey({ delay_hours: 24, message: "" })]); setDirty(true); };
  const removeStep = (i) => { setSteps((s) => s.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async () => {
    try {
      await api.put(`/sequences/${seq.id}`, {
        name,
        active,
        steps: steps.map((st) => ({ delay_hours: parseFloat(st.delay_hours) || 0, message: st.message })),
      });
      toast.success("Sequence saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["sequences"] });
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    await api.delete(`/sequences/${seq.id}`);
    toast.success("Sequence deleted");
    qc.invalidateQueries({ queryKey: ["sequences"] });
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={`sequence-${seq.id}`}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 flex-1">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-secondary ${meta.color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <Input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} className="font-semibold border-0 px-0 h-auto text-base focus-visible:ring-0 bg-transparent" data-testid={`sequence-name-${seq.id}`} />
            <p className="text-xs text-muted-foreground">{meta.label} · {seq.active_enrollments || 0} active</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Active</span>
            <Switch checked={active} onCheckedChange={(v) => { setActive(v); setDirty(true); }} data-testid={`sequence-active-${seq.id}`} />
          </div>
          <Button size="icon" variant="ghost" onClick={remove} data-testid={`delete-sequence-${seq.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
        </div>
      </div>

      <div className="space-y-3">
        {steps.map((st, i) => (
          <div key={st._k} className="rounded-xl border border-border bg-background/40 p-4" data-testid={`step-${seq.id}-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                <GripVertical className="h-3.5 w-3.5" /> Step {i + 1}
              </span>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Send after</span>
                <Input type="number" value={st.delay_hours} onChange={(e) => update(i, "delay_hours", e.target.value)} className="w-20 h-8" data-testid={`step-delay-${seq.id}-${i}`} />
                <span className="text-muted-foreground">hours</span>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeStep(i)}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
              </div>
            </div>
            <Textarea value={st.message} onChange={(e) => update(i, "message", e.target.value)} rows={2} placeholder="Message... use {name}" data-testid={`step-message-${seq.id}-${i}`} />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" size="sm" onClick={addStep} data-testid={`add-step-${seq.id}`}><Plus className="h-4 w-4 mr-1.5" /> Add step</Button>
        {dirty && (
          <Button size="sm" onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`save-sequence-${seq.id}`}>
            <Save className="h-4 w-4 mr-1.5" /> Save changes
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Sequences() {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const { data: sequences = [] } = useQuery({
    queryKey: ["sequences"],
    queryFn: () => api.get("/sequences").then((r) => r.data),
  });

  const createSeq = async () => {
    if (!newName.trim()) return;
    await api.post("/sequences", { name: newName, type: "booking", steps: [{ delay_hours: 0, message: "Hi {name}! " }] });
    toast.success("Sequence created");
    setNewName("");
    qc.invalidateQueries({ queryKey: ["sequences"] });
  };

  return (
    <div data-testid="sequences-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">SMS Drip Sequences</h1>
          <p className="text-muted-foreground mt-1">Multi-step texts that nudge leads until they install. Auto-stops when a lead replies or is Won; install drips roll into the booking drip.</p>
        </div>
        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New sequence name" className="w-52" data-testid="new-sequence-input" />
          <Button onClick={createSeq} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-sequence-button">
            <Plus className="h-4 w-4 mr-1.5" /> New
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {sequences.map((seq) => (
          <SequenceCard key={seq.id} seq={seq} />
        ))}
      </div>
    </div>
  );
}
